/**
 * NotificationPreferencesSection — Notification settings for all users.
 *
 * Allows users to control what notifications they receive and via which
 * channels (in-app and email). Enforces preferences in real-time.
 */

import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { ProfileCard, Pill } from './ProfilePrimitives';
import Button from '../common/Button';

const MUTED = '#7a7060';

// Default notification preferences - table structure for better organization
const DEFAULT_PREFERENCES = {
  assignments: {
    inApp: true,
    email: false,
    label: 'Assignments',
    description: 'Assigned, accepted, rejected, completed',
    category: 'work',
    priority: 'high',
  },
  companyAccess: {
    inApp: true,
    email: false,
    label: 'Company Access',
    description: 'Access granted, requested, reminders',
    category: 'work',
    priority: 'medium',
  },
  system: {
    inApp: true,
    email: false,
    label: 'System',
    description: 'Platform updates, maintenance, security',
    category: 'platform',
    priority: 'low',
  },
};
// Reusable toggle component for notification channels
const NotificationToggle = ({ label, description, isEnabled, onToggle, color }) => (
  <div className="flex items-center justify-between">
    <div>
      <div className="text-[12px] font-medium" style={{ color: '#08111f' }}>
        {label}
      </div>
      <div className="text-[10.5px]" style={{ color: MUTED }}>
        {description}
      </div>
    </div>
    <button
      type="button"
      onClick={onToggle}
      className="relative inline-flex h-6 w-11 items-center rounded-full transition-colors"
      style={{
        backgroundColor: isEnabled ? color : '#ddd6c6',
      }}
    >
      <span
        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${isEnabled ? 'translate-x-6' : 'translate-x-1'
          }`}
      />
    </button>
  </div>
);

export default function NotificationPreferencesSection({ user }) {
  const { updateProfile } = useAuth();
  const [preferences, setPreferences] = useState(DEFAULT_PREFERENCES);
  const [saving, setSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState('');

  // Load user's current preferences
  useEffect(() => {
    if (user?.notificationPreferences) {

      setPreferences({
        ...user.notificationPreferences
      });
    }
  }, [user]);

  const handleToggle = (category, channel) => {
    setPreferences(prev => ({
      ...prev,
      [category]: {
        ...prev[category],
        [channel]: !prev[category][channel],
      },
    }));
    // Clear any previous save message when user makes changes
    setSaveMessage('');
  };

  const handleSave = async () => {
    setSaving(true);
    setSaveMessage('');

    try {
      await updateProfile({ notificationPreferences: preferences });
      setSaveMessage('Notification preferences saved successfully!');

      // Clear success message after 3 seconds
      setTimeout(() => setSaveMessage(''), 3000);
    } catch (error) {
      console.error('Failed to save notification preferences:', error);
      setSaveMessage('Failed to save preferences. Please try again.');

      // Clear error message after 5 seconds
      setTimeout(() => setSaveMessage(''), 5000);
    } finally {
      setSaving(false);
    }
  };

  const hasChanges = JSON.stringify(preferences) !== JSON.stringify(
    user?.notificationPreferences || DEFAULT_PREFERENCES
  );

  return (
    <ProfileCard
      title="Notification Preferences"
      subtitle="Choose what notifications you receive and how"
      icon="🔔"
      action={
        hasChanges ? (
          <Button variant="primary" size="sm" onClick={handleSave} loading={saving}>
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        ) : null
      }
    >
      <div className="space-y-6">
        {/* Save Message */}
        {saveMessage && (
          <div
            className="px-3 py-2 rounded-lg text-[11.5px] font-medium"
            style={{
              backgroundColor: saveMessage.includes('success') ? '#e8efe5' : '#f2e9e6',
              color: saveMessage.includes('success') ? '#4a6b3f' : '#9a564a',
            }}
          >
            {saveMessage}
          </div>
        )}

        {/* Instructions */}
        <div className="text-[11.5px]" style={{ color: MUTED }}>
          Control which notifications you receive. In-app notifications appear in real-time,
          while email notifications are sent to your registered email address.
        </div>

        {/* Notification Categories Grid */}
        <div className="space-y-4">
          {Object.entries(preferences).map(([key, pref]) => (
            <div
              key={key}
              className="border rounded-lg p-4"
              style={{ borderColor: '#ddd6c6', backgroundColor: '#faf8f3' }}
            >
              {/* Category Header */}
              <div className="flex items-center justify-between mb-3">
                <div>
                  <h4 className="font-semibold text-[13px]" style={{ color: '#08111f' }}>
                    {pref.label}
                  </h4>
                  <div className="text-[11px]" style={{ color: MUTED }}>
                    {pref.description}
                  </div>
                </div>
                <div className="flex gap-2">
                  {pref.inApp && (
                    <Pill tone="green">In-App</Pill>
                  )}
                  {pref.email && (
                    <Pill tone="teal">Email</Pill>
                  )}
                  {!pref.inApp && !pref.email && (
                    <Pill tone="neutral">Off</Pill>
                  )}
                </div>
              </div>

              {/* Channel Toggles */}
              <div className="grid grid-cols-2 gap-4">
                <NotificationToggle
                  label="In-App Notifications"
                  description={pref.label ? `Receive ${pref.label} in-app notifications` : 'Receive notifications in-app'}
                  isEnabled={pref.inApp}
                  onToggle={() => handleToggle(key, 'inApp')}
                  color="#4a6b3f"
                />
                <NotificationToggle
                  label="Email Notifications"
                  description={pref.label ? `Receive ${pref.label} notifications via email` : 'Receive notifications via email'}
                  isEnabled={pref.email}
                  onToggle={() => handleToggle(key, 'email')}
                  color="#145f5f"
                />
              </div>
            </div>
          ))}
        </div>

        {/* Quick Actions */}
        <div className="flex gap-2 pt-4 border-t" style={{ borderColor: '#ddd6c6' }}>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPreferences(DEFAULT_PREFERENCES)}
          >
            Reset to Defaults
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              // Turn off all notifications
              const allOff = Object.keys(preferences).reduce((acc, key) => {
                acc[key] = { ...preferences[key], inApp: false, email: false };
                return acc;
              }, {});
              setPreferences(allOff);
              setSaveMessage('');
            }}
          >
            Turn Off All
          </Button>
          <Button
            variant="primary"
            size="sm"
            onClick={() => {
              // Turn on all in-app notifications
              const allOn = Object.keys(preferences).reduce((acc, key) => {
                acc[key] = { ...preferences[key], inApp: true };
                return acc;
              }, {});
              setPreferences(allOn);
              setSaveMessage('');
            }}
          >
            Enable In-App All
          </Button>
        </div>

        {/* Note about Email Delivery */}
        <div className="text-[10.5px]" style={{ color: MUTED }}>
          <strong>Note:</strong> Email notifications use the same email service as other platform
          communications. Delivery depends on your email provider and spam filters.
        </div>
      </div>
    </ProfileCard>
  );
}
